<script setup lang="ts">
import { computed } from "vue";
import { getSmallestThumbnailUrl, getTranslatedProperty } from "@shopware-pwa/helpers-next";
import type { Product } from "@shopware-pwa/types";
import { Tint } from "~/components/clarity-ui/Tint";
import type { EshopSetConfiguratorStepProduct } from "~/composables/shop/eShopSetConfiguratorTypes";
import ProductImage from "~/templates/elements/ProductImage.vue";
import { getValueSign } from "~/utils/helper/number";
import {
  getFirstColorOption,
  getFirstTextOption,
  hasFirstOption,
} from "~/utils/helper/shop/product";

export interface ProductSimpleOptionProps {
  stepProduct?: EshopSetConfiguratorStepProduct;
}

const { stepProduct } = defineProps<ProductSimpleOptionProps>();

const product = computed(() => stepProduct?.product);
const title = computed(() => getTranslatedProperty(product.value, "name"));
const firstColorOption = getFirstColorOption(product?.value as Product);
const firstTextOption = getFirstTextOption(product?.value as Product);
const absPriceDifference = computed(() => Math.abs(stepProduct?.differencePrice ?? 0));
const priceSign = computed(() => getValueSign(stepProduct?.differencePrice));
const mainImage = computed(() => getSmallestThumbnailUrl(product?.value?.cover?.media));
</script>

<template>
  <div class="flex items-center py-1 md:py-4">
    <div v-if="mainImage" class="w-18 shrink-0 py-1">
      <div class="aspect-square">
        <ProductImage
          :product-media="product?.cover?.media"
          main-class="object-contain w-full h-full"
        />
      </div>
    </div>
    <div class="flex min-w-0 flex-1 flex-col items-start gap-1 px-2">
      <span class="w-full hyphens-auto text-left text-sm font-medium">{{ title }}</span>
      <Tint v-if="hasFirstOption(product as Product)" class="text-sm">{{
        firstTextOption?.name || firstColorOption?.name
      }}</Tint>
      <span v-if="absPriceDifference && !stepProduct?.selected" class="inline-flex">
        <SharedPrice :value="absPriceDifference" class="text-sm">
          <template #beforePrice>{{ priceSign }}</template>
        </SharedPrice>
      </span>
    </div>
  </div>
</template>
